/**
 * Canton Bridge API Service
 *
 * Handles CC (Canton Coin) <-> USDC (Base) bridge operations
 * using the cantonlocal API. This is a temporary implementation
 * that will be updated when Canton joins global sync.
 *
 * Bridge Flow:
 * CC -> USDC: Lock CC on Canton -> Release USDC on Base
 * USDC -> CC: Deposit USDC on Base -> Release CC on Canton
 *
 * Base chain settings (chain ID, USDC address, RPC URL) switch via networkConfig.
 */

import { getCantonBridgeNetworkConfig } from './networkConfig';

// Package ID from `daml build` - update after deploying DAR
const PACKAGE_ID = '667c8afcfe4f0084bc63de4e46de0d0f2c7c3a6a911fa392cafcb0b842097a6a';

// Bridge configuration (reads Base settings dynamically from networkConfig)
export const CANTON_BRIDGE_CONFIG = {
  // Canton network settings
  canton: {
    chainId: 'canton',
    chainName: 'Canton',
    chainType: 'canton' as const,
  },
  // Base network settings - dynamic getter that reads from networkConfig
  get base() {
    const netConfig = getCantonBridgeNetworkConfig();
    return {
      chainId: netConfig.base.chainId,
      chainName: netConfig.base.chainName,
      chainType: 'evm' as const,
      usdcAddress: netConfig.base.usdcAddress,
      bridgeContractAddress: netConfig.base.bridgeContractAddress,
    };
  },
  // Bridge operator (will be configured via environment)
  bridgeOperator: {
    cantonParty: '', // Set at runtime - Canton party ID of the bridge operator
    baseAddress: '', // Set at runtime - EVM address of the bridge operator
  },
  // Fee configuration
  bridgeFeePercent: 0.001, // 0.1% fee
  minBridgeAmount: 1, // Minimum 1 USDC/CC
  maxBridgeAmount: 1000000, // Maximum 1M USDC/CC
  // Timing
  bridgeTimeoutSeconds: 1800, // 30 minutes
  estimatedBridgeTime: 180, // ~3 minutes for confirmation
  // Base RPC for balance checks - dynamic
  get baseRpcUrl() {
    return getCantonBridgeNetworkConfig().base.rpcUrl;
  },
};

/**
 * Check if bridge has sufficient USDC to cover a payout
 * @param amountUsdc - Amount in human readable format (e.g., "100.5")
 * @returns Object with hasSufficient flag and current balance
 */
export async function checkBridgeUsdcBalance(amountUsdc: string): Promise<{
  hasSufficient: boolean;
  bridgeBalance: string;
  requiredAmount: string;
  error?: string;
}> {
  const bridgeAddress = CANTON_BRIDGE_CONFIG.base.bridgeContractAddress;
  const usdcAddress = CANTON_BRIDGE_CONFIG.base.usdcAddress;
  const rpcUrl = CANTON_BRIDGE_CONFIG.baseRpcUrl;

  if (!bridgeAddress) {
    return {
      hasSufficient: false,
      bridgeBalance: '0',
      requiredAmount: amountUsdc,
      error: 'Bridge contract not configured',
    };
  }

  try {
    // ERC20 balanceOf(address) call
    // Function selector: 0x70a08231
    // Pad address to 32 bytes
    const paddedAddress = bridgeAddress.slice(2).toLowerCase().padStart(64, '0');
    const data = `0x70a08231${paddedAddress}`;

    const response = await fetch(rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'eth_call',
        params: [{ to: usdcAddress, data }, 'latest'],
      }),
    });

    const result = await response.json();
    if (result.error) {
      throw new Error(result.error.message);
    }

    // Parse balance (USDC has 6 decimals)
    const balanceRaw = BigInt(result.result || '0x0');
    const balanceUsdc = Number(balanceRaw) / 1e6;

    const requiredAmount = parseFloat(amountUsdc);
    const hasSufficient = balanceUsdc >= requiredAmount;

    return {
      hasSufficient,
      bridgeBalance: balanceUsdc.toFixed(2),
      requiredAmount: amountUsdc,
      error: hasSufficient ? undefined : `Bridge has insufficient USDC (${balanceUsdc.toFixed(2)} available, ${amountUsdc} required)`,
    };
  } catch (err) {
    return {
      hasSufficient: false,
      bridgeBalance: '0',
      requiredAmount: amountUsdc,
      error: `Failed to check bridge balance: ${err instanceof Error ? err.message : 'Unknown error'}`,
    };
  }
}

// Supported bridge tokens (USDC address reads dynamically from networkConfig)
export const CANTON_BRIDGEABLE_TOKENS: Record<string, {
  symbol: string;
  decimals: number;
  canton: { templateId: string };
  base: { address: string };
}> = {
  CC: {
    symbol: 'CC',
    decimals: 6,
    canton: {
      templateId: `${PACKAGE_ID}:CantonCoin.Bridge:BridgeEscrow`,
    },
    base: {
      address: '',
    },
  },
  get USDC() {
    return {
      symbol: 'USDC',
      decimals: 6,
      canton: {
        templateId: `${PACKAGE_ID}:CantonCoin.Bridge:BridgeRequest`,
      },
      base: {
        address: getCantonBridgeNetworkConfig().base.usdcAddress,
      },
    };
  },
} as any;

// Bridge chains
export type CantonBridgeChain = 'canton' | 'base';

// Oracle configuration for exchange rates
const ORACLE_CONFIG = {
  // CoinGecko API for live CC price
  coingeckoUrl: 'https://api.coingecko.com/api/v3/simple/price?ids=canton-network&vs_currencies=usd',
  // Fallback rate if oracle unavailable
  fallbackRate: 0.16, // 1 CC ≈ $0.16 USD
  // Rate cache duration
  cacheDurationMs: 60000, // 1 minute
};

// Cached rate
let cachedRate: { rate: number; timestamp: number } | null = null;

export interface CantonBridgeQuoteRequest {
  fromChain: CantonBridgeChain;
  toChain: CantonBridgeChain;
  fromSymbol: string; // 'CC' or 'USDC'
  toSymbol: string;   // 'USDC' or 'CC'
  amount: string;     // Human readable
}

export interface CantonBridgeQuote {
  fromChain: CantonBridgeChain;
  toChain: CantonBridgeChain;
  fromSymbol: string;
  toSymbol: string;
  amountIn: string;
  amountOut: string;
  exchangeRate: string;
  estimatedFee: string;
  estimatedTime: number; // seconds
  route: string[];
  isSupported: boolean;
  error?: string;
  // Additional data for execution
  bridgeData?: {
    direction: 'cc_to_usdc' | 'usdc_to_cc';
    templateId: string;
    nonce: number;
  };
}

export interface CantonBridgeExecuteParams {
  quote: CantonBridgeQuote;
  // Canton params (for CC -> USDC)
  cantonPartyId?: string;
  // EVM params (for USDC -> CC)
  evmAddress?: string;
  destinationAddress: string; // Where to receive funds
}

/**
 * Map chain name to CantonBridgeChain type
 */
export function mapToCantonBridgeChain(chainName: string): CantonBridgeChain | null {
  const normalized = chainName.toLowerCase().trim();

  // Canton chain aliases
  if (normalized === 'canton' || normalized === 'canton network' || normalized === 'canton-network') {
    return 'canton';
  }
  if (normalized === 'base') {
    return 'base';
  }

  return null;
}

/**
 * Check if a swap is a Canton bridge swap
 */
export function isCantonBridgeSwap(fromChain: string, toChain: string, fromSymbol: string, toSymbol: string): boolean {
  const fromBridgeChain = mapToCantonBridgeChain(fromChain);
  const toBridgeChain = mapToCantonBridgeChain(toChain);

  if (!fromBridgeChain || !toBridgeChain) return false;
  if (fromBridgeChain === toBridgeChain) return false;

  // Normalize symbols for case-insensitive comparison
  const fromSymbolUpper = fromSymbol.toUpperCase();
  const toSymbolUpper = toSymbol.toUpperCase();

  // Valid combinations:
  // CC (Canton) -> USDC (Base)
  // USDC (Base) -> CC (Canton)
  const isValidCCToUSDC = fromBridgeChain === 'canton' && toBridgeChain === 'base' &&
                          fromSymbolUpper === 'CC' && toSymbolUpper === 'USDC';
  const isValidUSDCToCC = fromBridgeChain === 'base' && toBridgeChain === 'canton' &&
                          fromSymbolUpper === 'USDC' && toSymbolUpper === 'CC';

  return isValidCCToUSDC || isValidUSDCToCC;
}

/**
 * Fetch CC/USD exchange rate from CoinGecko
 */
async function fetchExchangeRate(): Promise<number> {
  // Check cache first
  if (cachedRate && Date.now() - cachedRate.timestamp < ORACLE_CONFIG.cacheDurationMs) {
    return cachedRate.rate;
  }

  try {
    const response = await fetch(ORACLE_CONFIG.coingeckoUrl);
    if (!response.ok) {
      throw new Error(`CoinGecko HTTP ${response.status}`);
    }

    const data = await response.json();
    const rate = data['canton-network']?.usd;
    if (typeof rate !== 'number' || rate <= 0) {
      throw new Error('Invalid rate from CoinGecko');
    }

    // Update cache
    cachedRate = { rate, timestamp: Date.now() };
    return rate;

  } catch (error) {
    console.error('Canton Bridge: CoinGecko fetch failed, using fallback', error);
    return cachedRate?.rate ?? ORACLE_CONFIG.fallbackRate;
  }
}

/**
 * Generate unique nonce for bridge transaction
 */
function generateBridgeNonce(): number {
  return Date.now() * 1000 + Math.floor(Math.random() * 1000);
}

class CantonBridgeService {
  /**
   * Configure fallback rate (CoinGecko is used as primary)
   */
  setFallbackRate(rate: number): void {
    ORACLE_CONFIG.fallbackRate = rate;
  }

  /**
   * Get current CC/USD exchange rate
   */
  async getExchangeRate(): Promise<number> {
    return fetchExchangeRate();
  }

  /**
   * Get a bridge quote for CC <-> USDC
   */
  async getQuote(request: CantonBridgeQuoteRequest): Promise<CantonBridgeQuote> {
    const { fromChain, toChain, fromSymbol, toSymbol, amount } = request;

    // Validate the swap is supported
    const fromChainNormalized = mapToCantonBridgeChain(fromChain as string);
    const toChainNormalized = mapToCantonBridgeChain(toChain as string);

    if (!fromChainNormalized || !toChainNormalized) {
      return {
        ...request,
        amountIn: amount,
        amountOut: '0',
        exchangeRate: '0',
        estimatedFee: '0',
        estimatedTime: 0,
        route: [],
        isSupported: false,
        error: `Chain not supported: ${!fromChainNormalized ? fromChain : toChain}`,
      };
    }

    // Check if this is a valid bridge pair
    if (!isCantonBridgeSwap(fromChain, toChain, fromSymbol, toSymbol)) {
      return {
        ...request,
        amountIn: amount,
        amountOut: '0',
        exchangeRate: '0',
        estimatedFee: '0',
        estimatedTime: 0,
        route: [],
        isSupported: false,
        error: `Invalid bridge pair: ${fromSymbol} (${fromChain}) -> ${toSymbol} (${toChain}). Only CC <-> USDC supported.`,
      };
    }

    // Fetch exchange rate from oracle
    const ccUsdRate = await fetchExchangeRate();

    // Calculate amounts based on direction
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      return {
        ...request,
        amountIn: amount,
        amountOut: '0',
        exchangeRate: '0',
        estimatedFee: '0',
        estimatedTime: 0,
        route: [],
        isSupported: false,
        error: 'Invalid amount',
      };
    }

    let amountOut: number;
    let exchangeRateDisplay: string;
    let direction: 'cc_to_usdc' | 'usdc_to_cc';
    let templateId: string;

    // Normalize symbols for case-insensitive comparison
    const fromSymbolUpper = fromSymbol.toUpperCase();
    const toSymbolUpper = toSymbol.toUpperCase();

    if (fromSymbolUpper === 'CC' && toSymbolUpper === 'USDC') {
      // CC -> USDC: multiply by rate
      direction = 'cc_to_usdc';
      amountOut = amountNum * ccUsdRate;
      exchangeRateDisplay = `1 CC = ${ccUsdRate.toFixed(4)} USDC`;
      templateId = CANTON_BRIDGEABLE_TOKENS.CC.canton.templateId;
    } else {
      // USDC -> CC: divide by rate
      direction = 'usdc_to_cc';
      amountOut = amountNum / ccUsdRate;
      exchangeRateDisplay = `1 USDC = ${(1 / ccUsdRate).toFixed(4)} CC`;
      templateId = CANTON_BRIDGEABLE_TOKENS.USDC.canton.templateId;
    }

    // Bridge fee (0.1% for now - configurable)
    const feePercent = 0.001;
    const fee = amountOut * feePercent;
    const amountOutAfterFee = amountOut - fee;

    // Build route description
    const route = [
      `${fromSymbol} on ${fromChain === 'canton' ? 'Canton' : 'Base'}`,
      'Canton Bridge',
      `${toSymbol} on ${toChain === 'canton' ? 'Canton' : 'Base'}`,
    ];

    return {
      fromChain: fromChainNormalized,
      toChain: toChainNormalized,
      fromSymbol,
      toSymbol,
      amountIn: amount,
      amountOut: amountOutAfterFee.toFixed(6),
      exchangeRate: exchangeRateDisplay,
      estimatedFee: `~$${(fee * (direction === 'cc_to_usdc' ? 1 : ccUsdRate)).toFixed(4)}`,
      estimatedTime: CANTON_BRIDGE_CONFIG.estimatedBridgeTime,
      route,
      isSupported: true,
      bridgeData: {
        direction,
        templateId,
        nonce: generateBridgeNonce(),
      },
    };
  }

  /**
   * Get supported tokens for bridging
   */
  getSupportedTokens(): string[] {
    return Object.keys(CANTON_BRIDGEABLE_TOKENS);
  }

  /**
   * Get token info
   */
  getTokenInfo(symbol: string): typeof CANTON_BRIDGEABLE_TOKENS[string] | null {
    return CANTON_BRIDGEABLE_TOKENS[symbol.toUpperCase()] || null;
  }

  /**
   * Check if token is bridgeable
   */
  isBridgeable(symbol: string): boolean {
    return symbol.toUpperCase() in CANTON_BRIDGEABLE_TOKENS;
  }

  /**
   * Build CC -> USDC bridge transaction params
   * Returns data needed for SDK.bridgeCCToUSDC() call
   */
  buildCCToUSDCParams(
    quote: CantonBridgeQuote,
    cantonPartyId: string,
    destinationEvmAddress: string,
    bridgeOperatorPartyId?: string
  ): {
    templateId: string;
    payload: {
      owner: string;
      bridgeOperator: string;
      amount: string;
      destinationChain: string;
      destinationAddress: string;
      sourceChain: string;
      nonce: number;
      expiresAt: string;
      createdAt: string;
      status: string;
      releaseTxHash: null;
    };
  } {
    if (!quote.bridgeData || quote.bridgeData.direction !== 'cc_to_usdc') {
      throw new Error('Invalid quote for CC -> USDC bridge');
    }

    const now = new Date();
    const expiresAt = new Date(now.getTime() + CANTON_BRIDGE_CONFIG.bridgeTimeoutSeconds * 1000);

    return {
      templateId: quote.bridgeData.templateId,
      payload: {
        owner: cantonPartyId,
        bridgeOperator: bridgeOperatorPartyId || cantonPartyId,
        amount: quote.amountIn,
        destinationChain: 'base',
        destinationAddress: destinationEvmAddress,
        sourceChain: 'canton',
        nonce: quote.bridgeData.nonce,
        expiresAt: expiresAt.toISOString(),
        createdAt: now.toISOString(),
        status: 'Pending',
        releaseTxHash: null,
      },
    };
  }

  /**
   * Build USDC -> CC bridge transaction params
   * Returns data needed for EVM deposit and SDK.bridgeUSDCToCC() call
   */
  buildUSDCToCCParams(
    quote: CantonBridgeQuote,
    _evmSourceAddress: string, // Used for logging/tracking, actual deposit uses connected wallet
    cantonPartyId: string,
    _bridgeOperatorPartyId?: string // Reserved for future use with dedicated bridge party
  ): {
    evmDeposit: {
      to: string;
      value: string;
      data: string;
      chainId: number;
    };
    evmApproval: {
      to: string;
      value: string;
      data: string;
      chainId: number;
    };
    cantonParams: {
      recipient: string;
      usdcAmount: string;
      requestedCCAmount: string;
      nonce: number;
    };
  } {
    if (!quote.bridgeData || quote.bridgeData.direction !== 'usdc_to_cc') {
      throw new Error('Invalid quote for USDC -> CC bridge');
    }

    // Build EVM deposit transaction
    // User needs to:
    // 1. Approve USDC spending by bridge contract
    // 2. Call bridge.deposit(amount, cantonPartyId)
    const bridgeContractAddress = CANTON_BRIDGE_CONFIG.base.bridgeContractAddress;
    const usdcAddress = CANTON_BRIDGE_CONFIG.base.usdcAddress;
    const amountRaw = BigInt(Math.floor(parseFloat(quote.amountIn) * Math.pow(10, 6))); // USDC has 6 decimals

    if (!bridgeContractAddress) {
      throw new Error('Bridge contract address not configured. Please deploy solidity/CantonBridge.sol first.');
    }

    // Encode cantonPartyId as bytes for the deposit function
    // deposit(uint256 amount, string calldata cantonPartyId) function selector: 0x47e7ef24
    // Note: This is a simplified encoding. Production should use proper ABI encoding.
    const stringToHex = (str: string): string => {
      return Array.from(str).map(c => c.charCodeAt(0).toString(16).padStart(2, '0')).join('');
    };
    const cantonPartyIdHex = stringToHex(cantonPartyId);
    const depositData = `0x47e7ef24${amountRaw.toString(16).padStart(64, '0')}0000000000000000000000000000000000000000000000000000000000000040${(cantonPartyId.length).toString(16).padStart(64, '0')}${cantonPartyIdHex.padEnd(64, '0')}`;

    return {
      evmDeposit: {
        to: bridgeContractAddress,
        value: '0x0',
        data: depositData,
        chainId: parseInt(CANTON_BRIDGE_CONFIG.base.chainId),
      },
      // For approval transaction (user must approve first)
      evmApproval: {
        to: usdcAddress,
        value: '0x0',
        // approve(address spender, uint256 amount) - 0x095ea7b3
        data: `0x095ea7b3${bridgeContractAddress.slice(2).padStart(64, '0')}${amountRaw.toString(16).padStart(64, '0')}`,
        chainId: parseInt(CANTON_BRIDGE_CONFIG.base.chainId),
      },
      cantonParams: {
        recipient: cantonPartyId,
        usdcAmount: quote.amountIn,
        requestedCCAmount: quote.amountOut,
        nonce: quote.bridgeData.nonce,
      },
    };
  }

  // ------------------------------------------------------------------
  // General-purpose escrow & request builders (used by testnet pool)
  // ------------------------------------------------------------------

  /**
   * Build a BridgeEscrow payload for CC → any EVM chain.
   * This is the single source of truth for escrow creation.
   */
  buildEscrowPayload(params: {
    ownerParty: string;
    bridgeOperatorParty: string;
    amount: string;
    destinationChain: string;
    destinationAddress: string;
    nonce: number;
  }): { templateId: string; payload: Record<string, unknown> } {
    const now = new Date();
    const expiresAt = new Date(now.getTime() + CANTON_BRIDGE_CONFIG.bridgeTimeoutSeconds * 1000);

    return {
      templateId: CANTON_BRIDGEABLE_TOKENS.CC.canton.templateId,
      payload: {
        owner: params.ownerParty,
        bridgeOperator: params.bridgeOperatorParty,
        amount: params.amount,
        destinationChain: params.destinationChain,
        destinationAddress: params.destinationAddress,
        sourceChain: 'canton',
        nonce: params.nonce,
        expiresAt: expiresAt.toISOString(),
        createdAt: now.toISOString(),
        status: 'Pending',
        releaseTxHash: null,
      },
    };
  }

  /**
   * Build a BridgeRequest payload for any EVM chain → CC.
   * This is the single source of truth for bridge request creation.
   */
  buildBridgeRequestPayload(params: {
    recipientParty: string;
    bridgeOperatorParty: string;
    evmSourceAddress: string;
    evmTxHash: string;
    usdcAmount: string;
    requestedCCAmount: string;
    nonce: number;
  }): { templateId: string; payload: Record<string, unknown> } {
    return {
      templateId: CANTON_BRIDGEABLE_TOKENS.USDC.canton.templateId,
      payload: {
        recipient: params.recipientParty,
        bridgeOperator: params.bridgeOperatorParty,
        evmSourceAddress: params.evmSourceAddress,
        evmTxHash: params.evmTxHash,
        usdcAmount: params.usdcAmount,
        requestedCCAmount: params.requestedCCAmount,
        createdAt: new Date().toISOString(),
        status: 'Pending',
      },
    };
  }

  /** Expose the bridge nonce generator */
  generateNonce(): number {
    return generateBridgeNonce();
  }
}

// ============================================
// CANTON POLLING (CLIENT-SIDE)
// ============================================

export interface CantonBridgeEscrow {
  contractId: string;
  owner: string;
  amount: string;
  destinationAddress: string;
  nonce: number;
  status: 'Pending' | 'Completed' | 'Cancelled';
  createdAt: string;
}

export interface CantonBridgeRequest {
  contractId: string;
  recipient: string;
  usdcAmount: string;
  requestedCCAmount: string;
  evmTxHash: string;
  status: 'Pending' | 'Completed' | 'Failed';
  createdAt: string;
}

export interface CantonLedgerConfig {
  ledgerUrl: string;
  token: string;
  partyId: string;
}

/**
 * Client-side Canton polling service
 * Use this to poll Canton for bridge contract status
 */
class CantonPollingService {
  private config: CantonLedgerConfig | null = null;

  /**
   * Configure Canton ledger connection
   */
  configure(config: CantonLedgerConfig): void {
    this.config = config;
  }

  /**
   * Check if configured
   */
  isConfigured(): boolean {
    return this.config !== null;
  }

  /**
   * Query Canton contracts by template ID
   */
  private async queryContracts<T>(templateId: string): Promise<Array<{ contractId: string; payload: T }>> {
    if (!this.config) {
      throw new Error('Canton polling not configured. Call configure() first.');
    }

    const response = await fetch(`${this.config.ledgerUrl}/v1/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.config.token}`,
      },
      body: JSON.stringify({
        templateIds: [templateId],
        query: {},
        readers: [this.config.partyId],
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Canton query failed: ${error}`);
    }

    const result = await response.json() as { result: Array<{ contractId: string; payload: T }> };
    return result.result || [];
  }

  /**
   * Get all BridgeEscrow contracts (CC -> USDC requests)
   */
  async getEscrows(status?: 'Pending' | 'Completed' | 'Cancelled'): Promise<CantonBridgeEscrow[]> {
    const contracts = await this.queryContracts<Omit<CantonBridgeEscrow, 'contractId'>>(
      CANTON_BRIDGEABLE_TOKENS.CC.canton.templateId
    );

    const escrows: CantonBridgeEscrow[] = contracts.map(c => ({
      ...c.payload,
      contractId: c.contractId,
    }));

    if (status) {
      return escrows.filter(e => e.status === status);
    }
    return escrows;
  }

  /**
   * Get pending escrows (CC locked, waiting for USDC release)
   */
  async getPendingEscrows(): Promise<CantonBridgeEscrow[]> {
    return this.getEscrows('Pending');
  }

  /**
   * Get a specific escrow by contract ID
   */
  async getEscrow(contractId: string): Promise<CantonBridgeEscrow | null> {
    const escrows = await this.getEscrows();
    return escrows.find(e => e.contractId === contractId) || null;
  }

  /**
   * Get all BridgeRequest contracts (USDC -> CC requests)
   */
  async getBridgeRequests(status?: 'Pending' | 'Completed' | 'Failed'): Promise<CantonBridgeRequest[]> {
    const contracts = await this.queryContracts<Omit<CantonBridgeRequest, 'contractId'>>(
      CANTON_BRIDGEABLE_TOKENS.USDC.canton.templateId
    );

    const requests: CantonBridgeRequest[] = contracts.map(c => ({
      ...c.payload,
      contractId: c.contractId,
    }));

    if (status) {
      return requests.filter(r => r.status === status);
    }
    return requests;
  }

  /**
   * Get pending bridge requests (USDC deposited, waiting for CC release)
   */
  async getPendingBridgeRequests(): Promise<CantonBridgeRequest[]> {
    return this.getBridgeRequests('Pending');
  }

  /**
   * Get a specific bridge request by contract ID
   */
  async getBridgeRequest(contractId: string): Promise<CantonBridgeRequest | null> {
    const requests = await this.getBridgeRequests();
    return requests.find(r => r.contractId === contractId) || null;
  }

  /**
   * Poll for escrow status updates (CC -> USDC)
   * Returns a cleanup function to stop polling
   */
  pollEscrowStatus(
    contractId: string,
    onUpdate: (escrow: CantonBridgeEscrow | null) => void,
    intervalMs: number = 5000
  ): () => void {
    let active = true;

    const poll = async () => {
      if (!active) return;
      try {
        const escrow = await this.getEscrow(contractId);
        onUpdate(escrow);
      } catch (error) {
        console.error('Canton escrow poll error:', error);
      }
      if (active) {
        setTimeout(poll, intervalMs);
      }
    };

    poll();

    return () => {
      active = false;
    };
  }

  /**
   * Poll for bridge request status updates (USDC -> CC)
   * Returns a cleanup function to stop polling
   */
  pollBridgeRequestStatus(
    contractId: string,
    onUpdate: (request: CantonBridgeRequest | null) => void,
    intervalMs: number = 5000
  ): () => void {
    let active = true;

    const poll = async () => {
      if (!active) return;
      try {
        const request = await this.getBridgeRequest(contractId);
        onUpdate(request);
      } catch (error) {
        console.error('Canton bridge request poll error:', error);
      }
      if (active) {
        setTimeout(poll, intervalMs);
      }
    };

    poll();

    return () => {
      active = false;
    };
  }

  /**
   * Wait for escrow to be completed
   * Returns the completed escrow or throws on timeout/cancellation
   */
  async waitForEscrowCompletion(
    contractId: string,
    timeoutMs: number = 300000 // 5 minutes
  ): Promise<CantonBridgeEscrow> {
    const startTime = Date.now();

    while (Date.now() - startTime < timeoutMs) {
      const escrow = await this.getEscrow(contractId);

      if (!escrow) {
        throw new Error(`Escrow ${contractId} not found`);
      }

      if (escrow.status === 'Completed') {
        return escrow;
      }

      if (escrow.status === 'Cancelled') {
        throw new Error(`Escrow ${contractId} was cancelled`);
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, 5000));
    }

    throw new Error(`Escrow ${contractId} completion timed out`);
  }

  /**
   * Wait for bridge request to be completed
   * Returns the completed request or throws on timeout/failure
   */
  async waitForBridgeRequestCompletion(
    contractId: string,
    timeoutMs: number = 300000 // 5 minutes
  ): Promise<CantonBridgeRequest> {
    const startTime = Date.now();

    while (Date.now() - startTime < timeoutMs) {
      const request = await this.getBridgeRequest(contractId);

      if (!request) {
        throw new Error(`Bridge request ${contractId} not found`);
      }

      if (request.status === 'Completed') {
        return request;
      }

      if (request.status === 'Failed') {
        throw new Error(`Bridge request ${contractId} failed`);
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, 5000));
    }

    throw new Error(`Bridge request ${contractId} completion timed out`);
  }
}

export const cantonBridgeApi = new CantonBridgeService();
export const cantonPolling = new CantonPollingService();
export { CantonBridgeService, CantonPollingService };
